// ═══════════════════════════════════════════════════════════════════
//  MinimapModeToggle — three-icon switch between normal/density/flow.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase F of MINIMAP_ATLAS_PLAN. Renders three small icon buttons in
//  a row. Active mode highlighted. Emits onChange when the user picks
//  a different mode.
//
// ═══════════════════════════════════════════════════════════════════

import { Activity, Flame, Square } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MinimapMode } from '@/types/minimap';

export interface MinimapModeToggleProps {
  mode: MinimapMode;
  onChange: (mode: MinimapMode) => void;
  className?: string;
}

const MODES: Array<{ id: MinimapMode; label: string; Icon: LucideIcon }> = [
  { id: 'normal', label: 'Normal', Icon: Square },
  { id: 'heatmap-density', label: 'Density heatmap', Icon: Flame },
  { id: 'heatmap-flow', label: 'Flow heatmap', Icon: Activity },
];

export function MinimapModeToggle({ mode, onChange, className }: MinimapModeToggleProps) {
  return (
    <div
      className={cn('flex items-center gap-0.5 rounded border bg-background p-0.5', className)}
      role="radiogroup"
      aria-label="Minimap mode"
      data-fw-minimap-mode-toggle="true"
    >
      {MODES.map((m) => {
        const Icon = m.Icon;
        const isActive = m.id === mode;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={m.label}
            title={m.label}
            onClick={() => onChange(m.id)}
            className={cn(
              'flex h-4 w-4 items-center justify-center rounded transition-colors',
              isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
            )}
            data-fw-minimap-mode={m.id}
          >
            <Icon size={9} />
          </button>
        );
      })}
    </div>
  );
}
